import React, { useEffect, useMemo, useState, KeyboardEvent } from 'react';
import download from 'downloadjs';
import { useTranslation } from 'react-i18next';

import ViewCarousel from './viewer-carousel/ViewerCarousel';
import ImageViewer from './image-viewer/ImageViewer';
import VideoViewer from './video-viewer/VideoViewer';
import OtherFileViewer from './other-file-viewer/OtherFileViewer';
import { useAppFile } from '../../../hooks/use-app-file';
import { getFileType, FileType } from '../../../utils/file-utils';
import { Modal } from '@mui/material';
import { useStyles } from './AppDocsViewer.styles';

import theme from '../../../styles/theme';
import { INewFile } from '../../../types/interfaces/new-file';
import { IAppFile } from '../../../types/interfaces/app-file';
import { useLazyGetDownloadURL } from '../../../services/redux/api/fireBaseStorageApi';

type IFile = IAppFile | INewFile;

interface IAppDocsViewerProps {
  files: IFile[];
  selectedFile: IFile | null;
  setSelectedFile: (file: IFile | null) => void;
}

interface IFileData {
  id: string;
  url: string;
  name: string;
  type: FileType;
}

export default function AppDocsViewer({
  files,
  selectedFile,
  setSelectedFile,
}: IAppDocsViewerProps) {
  const [getFileUrl] = useLazyGetDownloadURL();
  const [data, setData] = useState<IFileData[]>([]);
  const [zoomValue, setZoomValue] = useState(1);
  const { t } = useTranslation();
  const { downloadFile } = useAppFile(t);
  const { classes } = useStyles(theme);

  const selectedFileIndex = useMemo(() => {
    if (!data || !selectedFile) return null;
    return data.findIndex((i) => i.id === selectedFile.id);
  }, [selectedFile, data]);

  useEffect(() => {
    if (!selectedFile) return;

    Promise.all(
      files.map(
        (file) =>
          new Promise((res, rej) => {
            if ('fileBlob' in file) {
              res(file.fileBlob);
              return;
            }

            getFileUrl(file.storageLink!)
              .then((response) => res(response.data))
              .then(res)
              .catch(rej);
          }).then(
            (url) =>
              ({
                id: file.id,
                url: url as string,
                name: file.name,
                type: getFileType(file.mimeType),
              }) as IFileData,
          ), ///????
      ),
    ).then((value: IFileData[]) => {
      setData(value);
    });
  }, [files, selectedFile]);

  useEffect(() => {
    const listener = (e: Event) => {
      const keyboardEvent = e as unknown as KeyboardEvent; //?????

      if (keyboardEvent.key === 'ArrowLeft') {
        previousClickHandler();
      } else if (keyboardEvent.key === 'ArrowRight') {
        nextClickHandler();
      } else if (keyboardEvent.key === 'Escape') {
        closeHandler();
      }
    };

    window.addEventListener('keydown', listener);
    return () => window.removeEventListener('keydown', listener);
  }, [files]);

  const selectedFileView = useMemo(() => {
    const file = data[selectedFileIndex!];

    if (!file) return;

    if (file?.type === FileType.image) {
      return (
        <ImageViewer
          src={file.url}
          alt={file.name}
          zoomValue={zoomValue}
          onClick={closeHandler}
        />
      );
    }

    if (file?.type === FileType.video) {
      return <VideoViewer url={file.url} />;
    }

    return <OtherFileViewer type={file.type} />;
  }, [selectedFileIndex, zoomValue]);

  const downloadFileHandler = () => {
    if (!selectedFile || !('storageLink' in selectedFile)) return;

    if (!!selectedFile.storageLink) {
      downloadFile(selectedFile.storageLink).then((result) => {
        download(result, selectedFile.name, selectedFile.mimeType);
      });
    }
  };

  function previousClickHandler() {
    setZoomValue(1);
    setSelectedFile(((prev) => {
      const index = files.findIndex((f) => f.id === prev?.id);
      return (
        files.find(
          (f) => f.id === data[(index + data.length - 1) % data.length]?.id,
        ) || null
      );
    }) as any);
  }

  function nextClickHandler() {
    setZoomValue(1);
    setSelectedFile(((prev) => {
      const index = files.findIndex((f) => f.id === prev?.id);
      return (
        files.find((f) => f.id === data[(index + 1) % data.length]?.id) || null
      );
    }) as any);
  }

  function onZoomClickHandler(value: number) {
    const newValue = zoomValue * value;
    if (newValue >= 1 && newValue <= 8) {
      setZoomValue(newValue);
    }
  }

  function closeHandler() {
    setZoomValue(1);
    setSelectedFile(null);
  }

  return (
    <div className={classes.viewerWrapper} onClick={(e) => e.stopPropagation()}>
      <Modal
        open={
          !!selectedFile &&
          typeof selectedFileIndex === 'number' &&
          selectedFileIndex !== -1
        }
        onClose={closeHandler}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(19, 15, 38, 0.4)',
        }}>
        <ViewCarousel
          onClose={closeHandler}
          selectedFileIndex={selectedFileIndex!}
          isImage={data[selectedFileIndex!]?.type === FileType.image}
          onDownloadClick={downloadFileHandler}
          onNextClick={nextClickHandler}
          onPreviousClick={previousClickHandler}
          onZoomClick={onZoomClickHandler}
          files={data}>
          {selectedFileView}
        </ViewCarousel>
      </Modal>
    </div>
  );
}
